import {Component, ElementRef, Input, NgZone, OnInit, ViewChild} from '@angular/core';
import {MapsAPILoader} from '@agm/core';

import {Address} from '../../domain/address';
import {AddressLookupService} from '../address-lookup.service';
import {GoogleAddressToAddressTransformer} from '../../transformers/google-address-to-address.transformer';
import {FormControl, FormGroup} from '@angular/forms';

interface GoogleMapsConfig {
  componentRestrictions?: any;
  mapStyle?: string;
}

@Component({
  selector: 'crds-google-places',
  template: `
    <div class="container">

      <div [formGroup]="form" class="form-group">
        <label class="form-label">Enter address</label>
        <input type="text" formControlName="googleAddressSearch" class="form-control" (keydown.enter)="$event.preventDefault()"
               placeholder="Search Nearest Location" autocapitalize="off" spellcheck="false"
               #search>
      </div>

      <agm-map
        [latitude]="latitude"
        [longitude]="longitude"
        [mapTypeId]="mapsConfig.mapStyle || defaultMapStyle"
        [streetViewControl]="false"
        [zoomControl]="true"
        [zoom]="zoom">
        <agm-marker [latitude]="latitude" [longitude]="longitude" [markerDraggable]="true"
                    (dragEnd)="markerDragEnd($event)"></agm-marker>
      </agm-map>

      <!-- QUESTION HINT -->
      <crds-question-hint [question]="question"></crds-question-hint>

    </div>
  `,
  styles: [
      `
      agm-map {
        height: 300px;
      }
    `
  ]
})
export class GooglePlacesComponent implements OnInit {

  @Input() mapsConfig?: GoogleMapsConfig = {};
  @Input() form: FormGroup; // Address Form Group
  @Input() question: any; // Address Form Group

  latitude: number;
  longitude: number;
  zoom: number;
  address: Address;
  // map style
  defaultMapStyle = 'roadmap';
  private geoCoder;

  @ViewChild('search')
  public searchElementRef: ElementRef;


  constructor(
    private mapsAPILoader: MapsAPILoader,
    private ngZone: NgZone,
    private addressLookupService: AddressLookupService,
    private googleAddressToAddressTransformer: GoogleAddressToAddressTransformer
  ) {
  }


  async ngOnInit() {

    const self = this;
    // load Places Autocomplete
    await self.mapsAPILoader.load();

    // Only set the current location if we don't have an existing search and potential address edits
    if (!self.form.get('googleAddressSearch').value) {
      self.setCurrentLocation();
    } else {
      if (self.form.get('latitude') && self.form.get('longitude')) {
        self.latitude = self.form.get('latitude').value;
        self.longitude = self.form.get('longitude').value;
        this.zoom = 17;
      }
    }
    self.geoCoder = new google.maps.Geocoder;

    const autocomplete = new google.maps.places.Autocomplete(self.searchElementRef.nativeElement, {
      types: ['address'],
      componentRestrictions: self.mapsConfig ? self.mapsConfig.componentRestrictions || null : null
    });

    const getPlace = (updateAddress = false) => {
      self.ngZone.run(() => {
        const place: google.maps.places.PlaceResult = autocomplete.getPlace();

        // verify result
        if (place.geometry === undefined || place.geometry === null) {
          return;
        }

        if (place) {
          // set latitude, longitude and zoom
          self.latitude = place.geometry.location.lat();
          self.longitude = place.geometry.location.lng();
          if (updateAddress) {
            self.getAddress(self.latitude, self.longitude);
          }
          self.zoom = 17;
        }
      });
    };

    autocomplete.addListener('place_changed', () => {
        getPlace(true);
    });
  }

  // Get Current Location Coordinates
  private setCurrentLocation() {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        this.latitude = position.coords.latitude;
        this.longitude = position.coords.longitude;
        this.zoom = 12;
        this.getAddress(this.latitude, this.longitude);
      });
    }
  }


  markerDragEnd($event: any) {
    console.log($event);
    this.latitude = $event.coords.lat;
    this.longitude = $event.coords.lng;
    this.getAddress(this.latitude, this.longitude);
  }

  getAddress(latitude, longitude) {
    this.geoCoder.geocode({'location': {lat: latitude, lng: longitude}}, (results, status) => {
      console.log(results);
      console.log(status);
      if (status === 'OK') {
        if (results[0]) {
          this.zoom = 17;

          const address = this.googleAddressToAddressTransformer.transform(results[0]);
          if (this.form.get('line1')) {
            this.form.get('line1').patchValue(address.line1);
          }
          if (this.form.get('line2')) {
            this.form.get('line2').patchValue(address.line2);
          }
          if (this.form.get('line3')) {
            this.form.get('line3').patchValue(address.line3);
          }
          if (this.form.get('line4')) {
            this.form.get('line4').patchValue(address.line4);
          }
          if (this.form.get('subLocality')) {
            this.form.get('subLocality').patchValue(address.subLocality);
          }
          if (this.form.get('locality')) {
            this.form.get('locality').patchValue(address.locality);
          }
          if (this.form.get('town')) {
            this.form.get('town').patchValue(address.town);
          }
          if (this.form.get('stateRegion')) {
            this.form.get('stateRegion').patchValue(address.stateRegion);
          }
          if (this.form.get('postCode')) {
            this.form.get('postCode').patchValue(address.postCode);
          }
          if (this.form.get('county')) {
            this.form.get('county').patchValue(address.county);
          }
          if (this.form.get('country')) {
            this.form.get('country').patchValue(address.country);
          }
          if (this.form.get('latitude')) {
            this.form.get('latitude').patchValue(address.latitude);
          } else {
            this.form.addControl('latitude', new FormControl(address.latitude));
          }
          if (this.form.get('longitude')) {
            this.form.get('longitude').patchValue(address.longitude);
          } else {
            this.form.addControl('longitude', new FormControl(address.longitude));
          }

          this.form.get('googleAddressSearch').patchValue(results[0].formatted_address);

        } else {
          window.alert('No results found');
        }
      } else {
        window.alert('Geocoder failed due to: ' + status);
      }

    });
  }
}
